// Fabrika phone PWA. Routes: #pair=<payload> (from a scanned QR), #d=<id>
// (a daemon's attention feed), #b=<id> (its backlog), #w=<id> (its work
// groups), #n=<id> (its notifications), #s=<id> (settings). Default launches
// the last-used daemon's feed; with nothing paired, the welcome/pair screen.
// You switch projects from the tappable title in the top-left (the switcher),
// not a dedicated tab.
import { renderPair, parsePairFragment } from "./views/pair.js";
import { renderFeed } from "./views/feed.js";
import { renderBoardView } from "./views/board.js";
import { renderAgentsView } from "./views/agents.js";
import { renderWork } from "./views/work.js";
import { renderSettings } from "./views/settings.js";
import { renderWelcome } from "./views/welcome.js";
import { renderNotifications } from "./views/notifications.js";
import { openScanner } from "./views/scanner.js";
import { openSwitcher } from "./switcher.js";
import { getDaemon, getLastDaemonId, listDaemons, setLastDaemonId } from "./store.js";
import type { DaemonEntry } from "./store.js";
import { el, clear } from "./dom.js";

const app = document.getElementById("app")!;
let cleanup: (() => void) | null = null;

function go(hash: string): void {
  location.hash = hash;
}

// Open the project switcher from the current daemon's title.
function openSwitch(currentId: string): void {
  openSwitcher({
    currentId,
    onSelect: (id) => go(`d=${id}`),
    onPair: () => openScanner((hash) => { location.hash = hash; }),
  });
}

// The feed is both an explicit route (#d=) and the default landing view, so
// build it in one place. Records the daemon as last-used for the next launch.
function feedFor(entry: DaemonEntry): () => void {
  setLastDaemonId(entry.daemonId);
  return renderFeed(
    app,
    entry,
    () => openSwitch(entry.daemonId),
    () => go(`b=${entry.daemonId}`),
    () => go(`s=${entry.daemonId}`),
    () => go(`n=${entry.daemonId}`),
  );
}

function route(): void {
  cleanup?.();
  cleanup = null;

  const pair = parsePairFragment(location.hash);
  if (pair) {
    renderPair(app, pair, (daemonId) => go(`d=${daemonId}`));
    return;
  }

  // Nothing paired: there's no project to scope to, so go straight to pairing.
  if (!listDaemons().length) {
    renderWelcome(app);
    return;
  }

  const feedMatch = location.hash.match(/#d=([a-f0-9]+)/);
  if (feedMatch) {
    const entry = getDaemon(feedMatch[1]);
    if (entry) {
      cleanup = feedFor(entry);
      return;
    }
  }

  const notifMatch = location.hash.match(/#n=([a-f0-9]+)/);
  if (notifMatch) {
    const entry = getDaemon(notifMatch[1]);
    if (entry) {
      cleanup = renderNotifications(
        app,
        entry,
        () => go(`d=${entry.daemonId}`),
        () => go(`d=${entry.daemonId}`),
        () => go(`b=${entry.daemonId}`),
        () => go(`s=${entry.daemonId}`),
      );
      return;
    }
  }

  const boardMatch = location.hash.match(/#b=([a-f0-9]+)/);
  if (boardMatch) {
    const entry = getDaemon(boardMatch[1]);
    if (entry) {
      cleanup = renderBoardView(
        app,
        entry,
        () => openSwitch(entry.daemonId),
        () => go(`d=${entry.daemonId}`),
        () => go(`s=${entry.daemonId}`),
        () => go(`n=${entry.daemonId}`),
      );
      return;
    }
  }

  const workMatch = location.hash.match(/#w=([a-f0-9]+)/);
  if (workMatch) {
    const entry = getDaemon(workMatch[1]);
    if (entry) {
      cleanup = renderWork(
        app,
        entry,
        () => openSwitch(entry.daemonId),
        () => go(`d=${entry.daemonId}`),
        () => go(`b=${entry.daemonId}`),
        () => go(`s=${entry.daemonId}`),
        () => go(`n=${entry.daemonId}`),
      );
      return;
    }
  }

  const agentsMatch = location.hash.match(/#a=([a-f0-9]+)/);
  if (agentsMatch) {
    const entry = getDaemon(agentsMatch[1]);
    if (entry) {
      cleanup = renderAgentsView(app, entry, {
        onSwitch: () => openSwitch(entry.daemonId),
        onFeed: () => go(`d=${entry.daemonId}`),
        onBoard: () => go(`b=${entry.daemonId}`),
        onSettings: () => go(`s=${entry.daemonId}`),
      });
      return;
    }
  }

  const settingsMatch = location.hash.match(/#s=([a-f0-9]+)/);
  if (settingsMatch) {
    const entry = getDaemon(settingsMatch[1]);
    if (entry) {
      renderSettings(app, entry, {
        onSwitch: () => openSwitch(entry.daemonId),
        onFeed: () => go(`d=${entry.daemonId}`),
        onBoard: () => go(`b=${entry.daemonId}`),
        onNotifications: () => go(`n=${entry.daemonId}`),
        onAgents: () => go(`a=${entry.daemonId}`),
        onReroute: () => { location.hash = ""; },
      });
      return;
    }
  }

  // Default: land in the last-used (or first) daemon's feed.
  const entry = getDaemon(getLastDaemonId()!);
  if (entry) {
    cleanup = feedFor(entry);
    return;
  }
  renderWelcome(app);
}

window.addEventListener("hashchange", route);

if (location.hash.startsWith("#pair=") && !window.isSecureContext) {
  clear(app);
  app.append(el("div", { class: "error-banner" }, ["Pairing requires HTTPS."]));
} else {
  route();
}

if ("serviceWorker" in navigator) {
  navigator.serviceWorker.register("/app/sw.js").catch((err: unknown) => {
    console.error("service worker registration failed", err);
  });
}
